import React, { useEffect, useState } from 'react';
import api from '../services/apiService';
import { Helmet } from 'react-helmet';
import { FOREIGN_ACCOUNTS, emptyForeignAccounts } from '../utils/foreignAccounts';

const emptyForm = () => ({ code: '', name: '', disableOfferAcceptance: false, ...emptyForeignAccounts() });
const sortCodes = codes => [...codes].sort((a, b) => a.code.localeCompare(b.code, 'de'));

function ForeignAccountFields({ value, onChange, scope, disabled }) {
  return (
    <div style={{ display: 'grid', gap: '8px', paddingTop: '8px' }}>
      {FOREIGN_ACCOUNTS.map(([field, label]) => (
        <label key={field} style={{ display: 'grid', gap: '4px' }}>
          {label}
          <input type="text" maxLength={255} aria-label={`${label} (${scope})`}
            value={value[field] || ''} disabled={disabled} placeholder="Nicht zugeordnet"
            onChange={event => onChange(previous => ({ ...previous, [field]: event.target.value }))} />
        </label>
      ))}
    </div>
  );
}

const AccountingCodeList = () => {
  const [accountingCodes, setAccountingCodes] = useState([]);
  const [newAccountingCode, setNewAccountingCode] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/accountingcodes')
      .then(response => setAccountingCodes(sortCodes(response.data)))
      .catch(() => setError('Fehler beim Abrufen der Buchungskonten.'))
      .finally(() => setLoading(false));
  }, []);

  const saveAccount = async (form, id = null) => {
    const code = form.code.trim();
    if (!code) { setError('Bitte ein Buchungskonto angeben.'); return; }
    setSaving(true);
    setError(null);
    const payload = { code, name: form.name.trim() || null, disableOfferAcceptance: !!form.disableOfferAcceptance,
      ...Object.fromEntries(FOREIGN_ACCOUNTS.map(([field]) => [field, form[field]?.trim() || null])) };
    try {
      const response = id
        ? await api.put(`/accountingcodes/${id}`, payload)
        : await api.post('/accountingcodes', payload);
      setAccountingCodes(previous => sortCodes(id
        ? previous.map(entry => entry.id === id ? response.data : entry)
        : [...previous, response.data]));
      if (id) setEditingId(null);
      else setNewAccountingCode(emptyForm());
    } catch (err) {
      setError(err?.response?.data?.error || 'Fehler beim Speichern des Buchungskontos.');
    } finally { setSaving(false); }
  };

  const deleteAccount = async id => {
    setSaving(true);
    setError(null);
    try {
      await api.delete(`/accountingcodes/${id}`);
      setAccountingCodes(previous => previous.filter(entry => entry.id !== id));
      if (editingId === id) setEditingId(null);
    } catch (err) {
      setError(err?.response?.data?.error || 'Fehler beim Löschen des Buchungskontos.');
    } finally { setSaving(false); }
  };

  if (loading) return <div>Lade Buchungskonten...</div>;
  return (
    <div className="p-4" style={{ maxWidth: '1200px', margin: 'auto' }}>
      <Helmet><title>Buchungskonten verwalten</title></Helmet>
      <h2>Buchungskonten</h2>
      <p>Die Artikel behalten ihr bisheriges Buchungskonto. Für Auslandsrechnungen wird je Steuerfall
        das hier zugeordnete Auslandskonto verwendet. Steuerschlüssel werden im FiBu-Programm gepflegt.</p>
      <p>Nur benötigte Auslandsfälle müssen ausgefüllt werden. Änderungen gelten für noch nicht exportierte Rechnungen.</p>
      {error && <p role="alert">{error}</p>}
      <form onSubmit={event => { event.preventDefault(); saveAccount(newAccountingCode); }}
        style={{ border: '1px solid #ccc', padding: '1rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input type="text" placeholder="Buchungskonto" aria-label="Neues Buchungskonto" value={newAccountingCode.code}
            disabled={saving} onChange={event => setNewAccountingCode(previous => ({ ...previous, code: event.target.value }))} />
          <input type="text" placeholder="Bezeichnung" aria-label="Neue Kontobezeichnung" value={newAccountingCode.name}
            disabled={saving} onChange={event => setNewAccountingCode(previous => ({ ...previous, name: event.target.value }))} />
          <label><input type="checkbox" checked={newAccountingCode.disableOfferAcceptance} disabled={saving}
            onChange={event => setNewAccountingCode(previous => ({ ...previous, disableOfferAcceptance: event.target.checked }))} />
            Angebotsannahme per Mail sperren</label>
          <button type="submit" disabled={saving}>Hinzufügen</button>
        </div>
        <details><summary>Auslandskonten (optional)</summary>
          <ForeignAccountFields value={newAccountingCode} onChange={setNewAccountingCode} scope="Neues Konto" disabled={saving} />
        </details>
      </form>
      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead><tr><th>ID</th><th>Buchungskonto</th><th>Bezeichnung</th><th>Mail-Annahme gesperrt</th><th>Auslandskonten</th><th>Aktionen</th></tr></thead>
          <tbody>{accountingCodes.map(account => (
            <tr key={account.id}>
              <td>{account.id}</td>
              <td>{editingId === account.id
                ? <input aria-label="Buchungskonto bearbeiten" value={editForm.code} disabled={saving}
                    onChange={event => setEditForm(previous => ({ ...previous, code: event.target.value }))} />
                : account.code}</td>
              <td>{editingId === account.id
                ? <input aria-label="Kontobezeichnung bearbeiten" value={editForm.name} disabled={saving}
                    onChange={event => setEditForm(previous => ({ ...previous, name: event.target.value }))} />
                : account.name || '–'}</td>
              <td>{editingId === account.id
                ? <label><input type="checkbox" checked={editForm.disableOfferAcceptance} disabled={saving}
                    onChange={event => setEditForm(previous => ({ ...previous, disableOfferAcceptance: event.target.checked }))} />Ja</label>
                : account.disableOfferAcceptance ? 'Ja' : 'Nein'}</td>
              <td>{editingId === account.id
                ? <ForeignAccountFields value={editForm} onChange={setEditForm} scope={`Konto ${account.code}`} disabled={saving} />
                : <dl style={{ margin: 0 }}>{FOREIGN_ACCOUNTS.map(([field, label]) => (
                    <React.Fragment key={field}><dt>{label}</dt><dd>{account[field] || '–'}</dd></React.Fragment>
                  ))}</dl>}</td>
              <td>{editingId === account.id ? <>
                <button type="button" disabled={saving} onClick={() => saveAccount(editForm, account.id)}>Speichern</button>
                <button type="button" disabled={saving} onClick={() => setEditingId(null)}>Abbrechen</button>
              </> : <>
                <button type="button" disabled={saving} onClick={() => {
                  setEditingId(account.id);
                  setEditForm({ ...emptyForm(), ...account, name: account.name || '', disableOfferAcceptance: !!account.disableOfferAcceptance });
                  setError(null);
                }}>Bearbeiten</button>
                <button type="button" disabled={saving} onClick={() => deleteAccount(account.id)}>Löschen</button>
              </>}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
};

export default AccountingCodeList;
